import React, {
  useCallback,
  useContext,
  useRef,
  useState,
  useMemo,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import InputField from 'components/InputField';
import EmailOrPhoneFieldWrapper from 'components/EmailOrPhoneFieldWrapper';
import Recaptcha, { RecaptchaRef } from 'components/Recaptcha';
import { Button, Loader } from 'ncoded-component-library';
import useValidations from '../../hooks/useValidations';
import AlternateLogins from '../../components/AlternateLogins';
import { Link } from 'react-router-dom';
import useQueryParams from 'hooks/useQueryParams';
import PasswordField from 'components/PasswordField';
import classNames from 'classnames';
import AuthContext from 'providers/Auth/Auth.context';
import {
  capitalizeName,
  getEmailOrPhoneForLoginOrRegister,
  getResolveUrl,
  getSellingPortfolio,
} from 'utils';
import HelmetWrapper from 'components/HelmetWrapper';
import { BUSINESS_ROUTE, LOGIN_ROUTE, SIGNUP_ROUTE } from 'data/global';
import useMatchMedia from 'hooks/useMatchMedia';
import {
  AuthBody,
  PhoneClaimTokenResponse,
  RegisterForm,
} from 'models/AuthBody';
import useMixpanelSignupInitiated from 'hooks/useMixpanelSignupInitiated';
import { SIGNUP_METHOD, SIGNUP_SOURCE } from 'models/MixpanelEvents';
import credentialsService from 'services/credentialsService';
import usePhoneNumberTransfer, {
  PHONE_TRANSFER_MODE,
} from 'hooks/usePhoneNumberTransfer';
import PhoneTransferModal from 'components/PhoneTransferModal';
import HintMessage from 'components/HintMessage';

import './SignUp.styles.scss';
import './SignUp.styles.responsive.scss';

type SignUpProps = {
  className?: string;
};

const SignUp: React.FC<SignUpProps> = () => {
  const { t } = useTranslation();
  const { popServerError, popError, popSuccess } = useContext(
    PopNotificationsContext,
  );
  const recaptchaRef = useRef<RecaptchaRef>(null);
  // The values that hit the conflict, replayed once the number is proven.
  const lastValuesRef = useRef<RegisterForm>();
  // The raw field entry the conflict belongs to. Both conflict states are about
  // one specific number, so they are shown only while that number is still in
  // the field - typing a different one drops them without any extra bookkeeping.
  const conflictInputRef = useRef<string>();
  const {
    params: { token, dealRoomId, listingEnquiryId },
  } = useQueryParams();
  const [isLoadingVals, setIsLoadingVals] = useState(false);
  const [defaultVals, setDefaultVals] = useState(null);
  const { onLoginReq } = useContext(AuthContext);

  const isPhablet = useMatchMedia('isPhablet');
  const commonValidations = useValidations();
  const classes = classNames('sign-up', { 'with-token': !!token });

  const signupInitiated = useMixpanelSignupInitiated();

  const phoneTransfer = usePhoneNumberTransfer({
    mode: PHONE_TRANSFER_MODE.SIGNUP,
  });

  const signupSource = useMemo(() => {
    if (!token) return SIGNUP_SOURCE.ORGANIC;

    if (dealRoomId) return SIGNUP_SOURCE.BROKER_INVITATION;

    if (listingEnquiryId) return SIGNUP_SOURCE.LEAD_INVITATION;

    return SIGNUP_SOURCE.ORGANIC;
  }, [dealRoomId, listingEnquiryId, token]);

  const completeSignup = useCallback(
    (authBody: AuthBody, phoneVerified: boolean) => {
      credentialsService.justRegistered();

      onLoginReq(
        authBody,
        dealRoomId ? getSellingPortfolio(dealRoomId) : BUSINESS_ROUTE,
      );

      if (phoneVerified) popSuccess(t('accountCreatedAndPhoneVerified'));
    },
    [dealRoomId, onLoginReq, popSuccess, t],
  );

  const register = useCallback(
    async (values: RegisterForm, phoneClaimToken?: string) => {
      // A claim token stands in for the captcha, so the retry neither reads nor
      // sends one. The widget expires ~120s after it is ticked and the SMS
      // round trip regularly outlasts that, so reading it here would fail the
      // signup on a "verify you are not a bot" error the user never earned.
      const recaptchaResponseToken = phoneClaimToken
        ? undefined
        : recaptchaRef.current?.getValue();

      if (!phoneClaimToken && !recaptchaResponseToken) {
        popError(t('recaptchaRequired'));
        return;
      }

      const { email, emailOrPhone, ...rest } = values;

      const emailOrPhoneForRequest = getEmailOrPhoneForLoginOrRegister({
        email,
        emailOrPhone,
      });

      const requestData = {
        ...(recaptchaResponseToken && { recaptchaResponseToken }),
        ...rest,
        ...emailOrPhoneForRequest,
        ...(phoneClaimToken && { phoneClaimToken }),
      };

      signupInitiated({
        socialSignup: false,
        signupSource,
        ...(emailOrPhoneForRequest.contactNumber
          ? {
              phoneNumber: emailOrPhoneForRequest.contactNumber,
              signupMethod: SIGNUP_METHOD.PHONE_NUMBER,
            }
          : {
              phoneNumber: emailOrPhoneForRequest.email,
              signupMethod: SIGNUP_METHOD.EMAIL,
            }),
      });

      try {
        const { data: authBody } = await api.auth.register(requestData);

        completeSignup(authBody, !!phoneClaimToken);
      } catch (e) {
        // The number is in use on another account: offer to claim it rather
        // than dead-ending on a toast. The captcha is deliberately NOT reset
        // here - the retry does not use it, and a user who instead dismisses
        // the popup and edits the number can submit again on the tick they
        // already gave.
        // auth/signup must accept a request with no
        // recaptchaResponseToken when a valid phoneClaimToken is present. The
        // retry cannot carry a usable token: Google's siteverify spends a v2
        // token on first use, and the token expires after ~120s anyway, which
        // the SMS round trip regularly outlasts. The claim token is itself
        // proof of a human holding the SIM, and the captcha was validated on
        // the call that raised the conflict moments earlier.
        if (
          phoneTransfer.onServerError(e, emailOrPhoneForRequest.contactNumber)
        ) {
          conflictInputRef.current = emailOrPhone;
          return;
        }

        recaptchaRef.current?.reset();

        popServerError(e);
      }
    },
    [
      signupSource,
      popError,
      t,
      signupInitiated,
      completeSignup,
      popServerError,
      phoneTransfer,
    ],
  );

  const onSubmit = useCallback(
    async (values: RegisterForm) => {
      phoneTransfer.reset();
      lastValuesRef.current = values;

      await register(values);
    },
    [phoneTransfer, register],
  );

  const onNumberConfirmed = useCallback(
    ({ phoneClaimToken }: PhoneClaimTokenResponse) => {
      phoneTransfer.reset();

      if (!lastValuesRef.current) return;

      register(lastValuesRef.current, phoneClaimToken);
    },
    [phoneTransfer, register],
  );

  useEffect(() => {
    if (token) {
      setIsLoadingVals(true);
      (async () => {
        try {
          const { data } = await api.auth.getPrefilingValues(token);
          setDefaultVals(data);
        } catch (e) {
          popServerError(e);
        } finally {
          setIsLoadingVals(false);
        }
      })();
    }
  }, [popServerError, token]);

  const preFormContent = useMemo(
    () =>
      token ? (
        <div className="signup-heading-container">
          <h1>{t('letsFinish')}</h1>
          <p>{t('beSureToCheckInfo')}</p>
        </div>
      ) : (
        <h1>{t('createAnAccount')}</h1>
      ),
    [t, token],
  );

  const initialValues = useMemo(() => {
    if (!defaultVals) return {};

    const { email, contactNumber, ...rest } = defaultVals;

    return { ...rest, emailOrPhone: email || contactNumber || '' };
  }, [defaultVals]);

  const helmetUrl = getResolveUrl(SIGNUP_ROUTE);
  const title = 'Create an account with Resolve';
  const description =
    'Create an account with Resolve to start buying or selling businesses today.';

  return (
    <>
      <HelmetWrapper title={title} description={description} url={helmetUrl} />
      {preFormContent}
      <Form
        onSubmit={onSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, submitting, valid, form, values }) => {
          const showPhoneConflict =
            values.emailOrPhone === conflictInputRef.current;

          // The tick is stale by now - the conflict path leaves it alone, and
          // it expires ~120s after it was given.
          const useAnotherNumber = () => {
            phoneTransfer.reset();
            form.change('emailOrPhone', '');
            recaptchaRef.current?.reset();
          };

          return (
            <form className={classes} onSubmit={handleSubmit}>
              <Field
                name="firstName"
                required
                placeholder={t('firstName')}
                label={t('firstName')}
                component={InputField}
                validate={commonValidations.name}
                formatOnBlur
                format={capitalizeName}
              />
              <Field
                name="lastName"
                required
                placeholder={t('lastName')}
                label={t('lastName')}
                component={InputField}
                validate={commonValidations.name}
                formatOnBlur
                format={capitalizeName}
              />

              <EmailOrPhoneFieldWrapper readOnly={!!token} />

              <Field
                name="password"
                required
                placeholder={t('password')}
                label={t('password')}
                component={PasswordField}
                validate={commonValidations.password}
              />

              {!token ? (
                <p className="create-acc">
                  {t('alreadyHaveAccount')}{' '}
                  <Link to={LOGIN_ROUTE}>{t('signIn')}</Link>
                </p>
              ) : (
                <span />
              )}

              <Recaptcha ref={recaptchaRef} ssgFallback={<span />} />

              {/* Sits on its own full-width row just above the submit button,
                  so the rest of the form keeps its two-column layout whether or
                  not a conflict is showing. */}
              {showPhoneConflict && phoneTransfer.isConflict && (
                <HintMessage
                  className="sign-up__phone-conflict"
                  type="info"
                  message={t('numberAlreadyVerifiedOnAnotherAccount')}
                  actionButtonText={t('confirmThisIsMyNumber')}
                  actionButtonVariant="outline"
                  onActionClick={phoneTransfer.openModal}
                />
              )}

              {showPhoneConflict && phoneTransfer.isLimitReached && (
                <HintMessage
                  className="sign-up__phone-limit"
                  type="error"
                  message={t('tooManyAttemptsOnThisNumber')}
                  actionButtonText={t('useAnotherNumber')}
                  actionButtonVariant="outline"
                  onActionClick={useAnotherNumber}
                />
              )}

              {!isPhablet && <span />}
              <div className="actions">
                <Button
                  type="submit"
                  disabled={!valid || submitting}
                  data-loading={submitting}
                >
                  {t(token ? 'completeYourAccount' : 'createAccount')}
                </Button>
              </div>
            </form>
          );
        }}
      />
      <AlternateLogins signupSource={signupSource} />
      {isLoadingVals && <Loader />}

      <PhoneTransferModal
        ref={phoneTransfer.modalRef}
        contactNumber={phoneTransfer.contactNumber}
        initiate={phoneTransfer.initiate}
        check={phoneTransfer.check}
        onSuccess={onNumberConfirmed}
        onServerError={phoneTransfer.onServerError}
      />
    </>
  );
};

export default SignUp;
